import { signIn } from "aws-amplify/auth";

export interface EmailCheckResult {
  exists: boolean;
  error?: string;
}

/**
 * Checks if a user exists in Cognito by attempting to sign in with a dummy password.
 * This is a common pattern since AWS doesn't provide a direct "user exists" API.
 *
 * @param email - The email address to check
 * @returns Promise<EmailCheckResult> - Result indicating if user exists
 */
export const checkIfUserExists = async (
  email: string
): Promise<EmailCheckResult> => {
  try {
    // Attempt to sign in with a dummy password
    // This will fail, but the error type tells us if the user exists
    await signIn({
      username: email,
      password: "dummy-password-that-will-never-work",
    });

    // If we somehow get here, the user exists (extremely unlikely)
    return { exists: true };
  } catch (error: any) {
    console.log("Email check error for", email, ":", error);

    // Check the error type to determine if user exists
    if (error.name === "NotAuthorizedException") {
      // User exists but password is wrong
      console.log("User exists (NotAuthorizedException):", email);
      return { exists: true };
    } else if (error.name === "UserNotFoundException") {
      // User doesn't exist
      console.log("User does not exist (UserNotFoundException):", email);
      return { exists: false };
    } else if (error.name === "UserNotConfirmedException") {
      // User exists but not confirmed
      console.log(
        "User exists but not confirmed (UserNotConfirmedException):",
        email
      );
      return { exists: true };
    } else if (error.name === "TooManyRequestsException") {
      // Rate limited - assume user exists to avoid blocking legitimate users
      console.log("Rate limited, assuming user exists:", email);
      return {
        exists: true,
        error: "Too many requests. Please try again later.",
      };
    } else if (error.name === "InvalidPasswordException") {
      // This can happen when user exists but has force change password status
      // The dummy password triggers this instead of NotAuthorizedException
      console.log(
        "User exists with force change password status (InvalidPasswordException):",
        email
      );
      return { exists: true };
    } else {
      // Check if this is a challenge response indicating user exists
      // but needs to complete a challenge (like force change password)
      const result = error as any;
      if (
        result?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD" ||
        result?.nextStep?.signInStep === "NEW_PASSWORD_REQUIRED" ||
        result?.nextStep?.signInStep ===
          "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        // User exists but needs to set a new password
        console.log(
          "User exists with force change password status (challenge response):",
          email
        );
        return { exists: true };
      }

      // Additional check for any other challenge responses that indicate user exists
      if (result?.nextStep) {
        console.log(
          "User exists with challenge (nextStep present):",
          email,
          "challenge:",
          result.nextStep
        );
        return { exists: true };
      }

      // Other errors - assume user doesn't exist to allow signup
      console.warn(
        "Unexpected error during email check for",
        email,
        ":",
        error
      );
      return {
        exists: false,
        error: "Unable to verify email. Please try again.",
      };
    }
  }
};
